import React, { FC, ReactNode } from 'react';

import { getIconTransform } from '@diagram/helpers/ComponentIconTransform';
import { ComponentInstanceHelpers } from '@diagram/helpers/ComponentInstanceHelpers';

import {
    Component,
    ComponentTeaser,
    ComponentType,
    DiagramComponentInstance,
    DiagramComponentInstanceConverter,
    DiagramConnection,
    PortFilter,
} from 'models';

import { customIcons } from 'components/component-icons/custom-icons';

import { ComponentIconLabel } from './ComponentIconLabel';
import { IconName, resolveIconName } from './icon-names';
import { getIconPathData, PathData } from './icon-paths';
import { DiagramConverterIcon } from './icons/DiagramConverterIcon/DiagramConverterIcon';
import { getPortConnections, getPortFilterConnections } from './icons/DiagramConverterIcon/DiagramConverterIconUtils';

const ComponentIcon: FC<{
    type: Component['type'];
    custom?: string | null;
    component?: Component | ComponentTeaser;
    componentInstance?: DiagramComponentInstance;
    connections?: DiagramConnection[];
    color?: string;
    symbol?: IconName;
    resizing?: boolean;
    ports?: PortFilter[];
    children?: ReactNode;
}> = ({
    type,
    custom = null,
    component = undefined,
    componentInstance = undefined,
    connections = undefined,
    color,
    symbol,
    resizing,
    ports,
    children,
}) => {
    const solutionComponentReplacement =
        type === 'solution' &&
        componentInstance?.componentType === 'solution' &&
        'baseComponents' in componentInstance &&
        Array.isArray(componentInstance.baseComponents) &&
        componentInstance.baseComponents.length === 1
            ? componentInstance.baseComponents[0]
            : null;

    const componentType = (solutionComponentReplacement ?? type) as ComponentType;

    const specifications =
        componentInstance?.specifications ??
        (component && 'electrical' in component ? { electrical: component.electrical } : undefined);

    const iconName = symbol ?? resolveIconName({ type: componentType, custom, specifications });

    const customIcon = customIcons.find((icon) => icon.type === iconName);

    if (customIcon) {
        return <customIcon.Icon size="100%" strokeWidth={1.25} />;
    }

    if (componentType === 'converter' && componentInstance && connections) {
        const isSmallConverter = componentInstance.rowSpan === 1 && componentInstance.colSpan === 1;

        if (isSmallConverter) {
            if (ports && ports.length > 0) {
                const portConnections = getPortFilterConnections(ports);
                return (
                    <svg
                        viewBox="0 0 40 40"
                        style={{
                            outlineOffset: '2px',
                            outline: '2px solid transparent',
                            border: '2px solid var(--mantine-color-gray-5)',
                            borderRadius: 'var(--mantine-radius-sm)',
                        }}
                        data-diagram-converter-icon
                    >
                        <DiagramConverterIcon portConnections={portConnections} color={color} />
                    </svg>
                );
            }

            if (!resizing) {
                const portConnections = getPortConnections({
                    componentInstance: componentInstance as DiagramComponentInstanceConverter,
                    connections,
                });
                return (
                    <svg viewBox="0 0 40 40" data-diagram-converter-icon>
                        <DiagramConverterIcon portConnections={portConnections} color={color} />
                    </svg>
                );
            }
        }
    }

    const iconData = getIconPathData(iconName);

    if (!iconData || !iconData.paths.length) {
        const componentDefinition = ComponentInstanceHelpers.definition(componentType);

        if (componentDefinition?.indicator) {
            return (
                <ComponentIconLabel
                    componentInstance={{
                        rowSpan: componentInstance?.rowSpan ?? 1,
                        colSpan: componentInstance?.colSpan ?? 1,
                        label: componentDefinition.indicator,
                    }}
                />
            );
        }

        return null;
    }

    const fill = color ?? 'currentColor';

    const uprightTransform =
        componentInstance && connections
            ? getIconTransform(componentInstance, connections, iconData.viewBox)?.upright
            : undefined;

    return (
        <svg
            viewBox={iconData.viewBox}
            fill={fill}
            xmlns="http://www.w3.org/2000/svg"
            data-icon-fills-cell={iconData.fillsCell || undefined}
        >
            {iconData.paths.map((pathData: PathData, index: number) => {
                if (typeof pathData === 'string') {
                    return <path key={index} d={pathData} fill={fill} />;
                }

                const path = (
                    <path
                        key={index}
                        d={pathData.d}
                        fill={pathData.fill ?? fill}
                        stroke={pathData.stroke}
                        strokeWidth={pathData.strokeWidth}
                        fillRule={pathData.fillRule}
                        clipRule={pathData.clipRule}
                        strokeLinecap={pathData.strokeLinecap}
                        strokeLinejoin={pathData.strokeLinejoin}
                    />
                );

                if (pathData.keepUpright && uprightTransform) {
                    return (
                        <g key={index} transform={uprightTransform}>
                            {path}
                        </g>
                    );
                }

                return path;
            })}
            {children}
        </svg>
    );
};

export { ComponentIcon };
